import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { collection, doc, Timestamp } from "firebase/firestore";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";
import { db } from "@/lib/firebase";
import { getOne, createItem, updateItem, listAll } from "@/lib/adminRepository";
import RichTextEditor from "@/components/admin/RichTextEditor";
import ImageUploader from "@/components/admin/ImageUploader";
import { stripHtml } from "@/lib/utils";
import { ADMIN } from "@/constants/testIds";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// yyyy-mm-dd (date input) → "10 Jun 2026" display label.
const displayDate = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return "";
  return `${d} ${MONTHS[m - 1]} ${y}`;
};

// Firestore Timestamp | ISO string → yyyy-mm-dd for the date input.
const toDateInput = (v) => {
  if (!v) return "";
  if (typeof v?.toDate === "function") return v.toDate().toISOString().slice(0, 10);
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const BLANK = {
  slug: "", title: "", category: "", excerpt: "", body: "",
  publishDate: "", readTime: "", image: "", status: "draft",
  metaTitle: "", metaDesc: "", keywords: [],
};

const Label = ({ children }) => <span className="editorial-label">{children}</span>;
const Hint = ({ children }) => <span className="block text-[11px] text-olive/80 mb-1">{children}</span>;

export default function BlogForm() {
  const { slug: routeSlug } = useParams();
  const isNew = !routeSlug || routeSlug === "new";
  const navigate = useNavigate();

  const [form, setForm] = useState(BLANK);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(!isNew);
  // Keywords are edited as free text (so spaces/commas type normally) and only
  // split into an array on save.
  const [keywordsText, setKeywordsText] = useState("");

  // Stable Storage folder so cover/inline uploads work before the first save.
  const uploadId = useMemo(
    () => (isNew ? doc(collection(db, "blogs")).id : routeSlug),
    [isNew, routeSlug]
  );
  const storagePath = `media/blogs/${uploadId}`;

  // Load the post being edited.
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getOne("blogs", routeSlug);
        if (cancelled) return;
        if (data) {
          setForm({ ...BLANK, ...data, publishDate: toDateInput(data.publishAt || data.date) });
          setKeywordsText(Array.isArray(data.keywords) ? data.keywords.join(", ") : data.keywords || "");
        } else toast.error("Post not found.");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[blog] load failed:", err);
        toast.error("Failed to load post.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isNew, routeSlug]);

  // Load categories (managed collection, with fallback to distinct post categories).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cats = await listAll("blogCategories");
        if (cancelled) return;
        if (cats.length) {
          setCategories(cats.map((c) => c.name).filter(Boolean));
        } else {
          const posts = await listAll("blogs");
          if (cancelled) return;
          setCategories(Array.from(new Set(posts.map((p) => p.category).filter(Boolean))));
        }
      } catch {
        /* leave empty — admin can still type via Manage categories */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v && v.target ? v.target.value : v }));

  // Auto-slug from title until the admin edits the slug directly.
  useEffect(() => {
    if (!slugTouched && form.title) setForm((f) => ({ ...f, slug: slugify(f.title) }));
  }, [form.title, slugTouched]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const slug = (isNew ? form.slug.trim() || slugify(form.title) : routeSlug);
    if (!form.title.trim()) return toast.error("Title is required.");
    if (!slug) return toast.error("Slug is required (or enter a title to auto-generate one).");

    setSaving(true);
    try {
      const { id, publishDate, ...rest } = form;
      const words = stripHtml(form.body).trim().split(/\s+/).filter(Boolean).length;
      const readTime = form.readTime.trim() || (words ? `${Math.max(1, Math.round(words / 200))} min read` : "");
      const payload = {
        ...rest,
        slug,
        readTime,
        keywords: keywordsText.split(",").map((s) => s.trim()).filter(Boolean),
        date: displayDate(publishDate) || form.date || "",
        publishAt: publishDate ? Timestamp.fromDate(new Date(`${publishDate}T00:00:00`)) : null,
      };
      if (isNew) {
        await createItem("blogs", payload, slug);
        toast.success("Post created.");
      } else {
        await updateItem("blogs", slug, payload);
        toast.success("Post saved.");
      }
      navigate("/admin/blogs");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[blog] save failed:", err);
      toast.error("Save failed: " + (err?.message || "error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-olive">Loading…</p>;

  // Ensure the post's current category is selectable even if it was removed.
  const catOptions = form.category && !categories.includes(form.category)
    ? [form.category, ...categories]
    : categories;

  return (
    <div data-testid="admin-blog-form">
      <Link to="/admin/blogs" className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.22em] uppercase text-bronze hover:text-bronze_dark mb-5">
        <ArrowLeft size={12} /> Back to blog
      </Link>
      <header className="mb-7">
        <div className="eyebrow text-bronze mb-1">{isNew ? "New" : "Edit"}</div>
        <h1 className="font-serif text-3xl md:text-4xl text-ink">{isNew ? "New Post" : form.title || "Edit post"}</h1>
      </header>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-[1fr,300px] gap-6">
        {/* Main column */}
        <div className="space-y-6">
          <section className="border border-bronze/20 bg-cream p-6 space-y-5">
            <label className="block">
              <Label>Title *</Label>
              <input className="editorial-input" value={form.title} onChange={set("title")} data-testid="blog-title" placeholder="e.g. The Quiet Case for Decoupling" />
            </label>

            <label className="block">
              <Label>Slug (URL)</Label>
              {isNew ? (
                <>
                  <Hint>Auto-generated from the title — edit to override. Used in /resources/blogs/&lt;slug&gt;.</Hint>
                  <input className="editorial-input" value={form.slug} onChange={(e) => { setSlugTouched(true); set("slug")(e); }} data-testid="blog-slug" placeholder="quiet-case-for-decoupling" />
                </>
              ) : (
                <input className="editorial-input opacity-70" value={routeSlug} readOnly data-testid="blog-slug" />
              )}
            </label>

            <div className="grid sm:grid-cols-2 gap-5">
              <label className="block">
                <Label>Category</Label>
                <Hint>
                  Drives the filter tag. <Link to="/admin/blogs/categories" className="text-bronze underline">Manage categories</Link>
                </Hint>
                <select className="editorial-input" value={form.category} onChange={set("category")} data-testid="blog-category">
                  <option value="">— None —</option>
                  {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block">
                <Label>Publish date</Label>
                <Hint>Sorts newest-first; also shown on the card.</Hint>
                <input type="date" className="editorial-input" value={form.publishDate} onChange={set("publishDate")} data-testid="blog-date" />
              </label>
            </div>

            <label className="block">
              <Label>Excerpt</Label>
              <Hint>One or two lines shown on the blog index card and search.</Hint>
              <textarea rows={2} className="editorial-input resize-y" value={form.excerpt} onChange={set("excerpt")} data-testid="blog-excerpt" placeholder="A short teaser for the article." />
            </label>
          </section>

          <section className="border border-bronze/20 bg-cream p-6">
            <div className="eyebrow text-bronze mb-1">Content</div>
            <h2 className="font-serif text-lg text-ink mb-3">Body</h2>
            <RichTextEditor
              value={form.body}
              onChange={set("body")}
              placeholder="Write the article — use the toolbar for headings, bold, lists, quotes, links, and images."
            />
          </section>

          <section className="border border-bronze/20 bg-cream p-6">
            <div className="eyebrow text-bronze mb-1">Media</div>
            <h2 className="font-serif text-lg text-ink mb-3">Cover image</h2>
            <ImageUploader path={storagePath} value={form.image ? [form.image] : []} onChange={(urls) => set("image")(urls[0] || "")} single testid="blog-cover" />
            <p className="text-[11px] text-olive mt-3">Leads the card and the article header.</p>
          </section>

          <section className="border border-bronze/20 bg-cream p-6 space-y-5">
            <div className="eyebrow text-bronze mb-1">SEO</div>
            <label className="block">
              <Label>Meta title</Label>
              <Hint>Defaults to the post title if left blank. Shown in search results &amp; browser tab.</Hint>
              <input className="editorial-input" value={form.metaTitle} onChange={set("metaTitle")} data-testid="blog-meta-title" placeholder="Defaults to the post title." />
            </label>
            <label className="block">
              <Label>Meta description</Label>
              <Hint>Defaults to the excerpt. ~150–160 characters for search snippets.</Hint>
              <textarea rows={2} className="editorial-input resize-y" value={form.metaDesc} onChange={set("metaDesc")} data-testid="blog-meta-desc" placeholder="A concise summary for search engines." />
            </label>
            <label className="block">
              <Label>Keywords</Label>
              <Hint>Comma-separated, e.g. HDB, upgrading, Singapore property.</Hint>
              <input
                className="editorial-input"
                value={keywordsText}
                onChange={(e) => setKeywordsText(e.target.value)}
                data-testid="blog-keywords"
                placeholder="HDB, upgrading, Singapore property"
              />
            </label>
          </section>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <section className="border border-bronze/20 bg-cream p-6 space-y-5">
            <label className="block">
              <Label>Visibility</Label>
              <Hint>Drafts are hidden from /resources/blogs and not addressable by slug.</Hint>
              <select className="editorial-input" value={form.status} onChange={set("status")} data-testid="blog-status">
                <option value="published">published</option>
                <option value="draft">draft</option>
              </select>
            </label>
            <label className="block">
              <Label>Read time</Label>
              <Hint>Optional — auto-estimated from the body if blank.</Hint>
              <input className="editorial-input" value={form.readTime} onChange={set("readTime")} data-testid="blog-readtime" placeholder="6 min read" />
            </label>
          </section>

          <div className="flex gap-3">
            <Link to="/admin/blogs" className="btn-secondary flex-1 text-center">Cancel</Link>
            <button type="submit" disabled={saving} data-testid={ADMIN.saveButton} className="btn-primary flex-1 justify-center disabled:opacity-60">
              <Save size={14} /> {saving ? "Saving…" : isNew ? "Create" : "Save"}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}
